/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

// The current route syntax originated with the Kendo Router.
// We support the required, optional, and extra params route syntax of the Kendo Router.
// We do NOT (yet) support the 'Globbed Routes' (also known as 'wildcard' based on '*' character).
// The wildcard param must terminate the route (nothing after it), and will match everything
// else up to the extra params '?' delimiter, such as '/foo/:bar/*restOfRoute'.
//
// See https://www.telerik.com/blogs/unleashing-power-kendo-uis-router
// specifically the 'Advanced Routing -> Globbed routes' subsection.

/**
 * Converts a route template with parameters into a regular expression.
 * Parameters are defined using the `:paramName` syntax.
 * Sections in parentheses are treated as optional.
 *
 * @param routeTemplate - The route template pattern
 * @returns An object containing the regular expression and parameter names in order
 */
export function routeTemplateToRegex(routeTemplate) {
  // Extract parameter names from the route template
  const paramNames = calcParamNames(routeTemplate);

  // Handle optional parts (parentheses)
  let processedTemplate = processParentheses(routeTemplate);

  // Now build a regex STRING from the processedTemplate.
  processedTemplate = processedTemplate
  // Replace parameter names with a capture groups so that regex will capture the parameter value
  .replace(/:\w+/g, '([^/]+)')
  // Escape forward slashes
  .replace(/\//g, '\\/')
  // Escape dots, if any
  .replace(/\./g, '\\.');

  // Create the final regex with start and end anchors
  const regex = new RegExp(`^${processedTemplate}$`);
  return {
    routeRegEx: regex,
    paramNames
  };
}
const PARAM_NAMES_REGEX = /:(\w+)/g;
export function calcParamNames(routeTemplate) {
  // Extract parameter names from the route template
  const paramNames = [];
  const paramMatches = routeTemplate.matchAll(PARAM_NAMES_REGEX);
  for (const match of paramMatches) {
    paramNames.push(match[1]); // matched group for param
  }
  return paramNames;
}

// innermost optional parens () that don't contain other parens
export const NESTED_PARENS_REGEX = /\(([^()]*)\)/;

/**
 * Processes parentheses in a route template recursively
 *
 * @param template - The template to process
 * @returns The processed template with parentheses converted to regex optional groups
 */
function processParentheses(template, depth = 1) {
  // Base case: no more parentheses

  // Defensive: bad route template might cause infinite recursion?  Abort after depth=100 to avoid crash.
  if (depth >= 100) {
    throw new Error(`processParentheses hit depth=${depth}, template='${template}'`);
  }
  // Find the most deeply nested pair of parentheses
  // This regex finds content in parentheses with no other parentheses inside.
  // Ignores parentheses already processed as they are temporarily represented as '[' and ']'
  const match = template.match(NESTED_PARENS_REGEX);
  if (!match) {
    // Munge returned value to get [?:.*]? back to desired (?:.*)? regex (see comment below)
    // Could probably get away with replacing all [ with ), and ] with ),
    // but below a bit safer in case future regexes also include square brackets.
    return template.replaceAll("[?:", "(?:").replaceAll("]?", ")?");
  }

  // Replace this pair with optional group syntax.
  // Replace string '(?:$1)?' is correct, but subsequent searches for more parentheses
  // will match it, and lookahead gets very complex very fast.
  // Much simpler to temporarily replace with '[?:$1]?', and then in above
  // !match above (recursion termination) do a replaceAll to munge to desired (?:.*)? regex.
  const updatedTemplate = template.replace(NESTED_PARENS_REGEX, '[?:$1]?' // '(?:$1)?'   /foo/:val    /foo/(?:$1)?    /foo/43
  );

  // Recursively process the rest
  return processParentheses(updatedTemplate, depth + 1);
}

/**
 * Attempts to match a given URL path against a route template
 *
 * @param routeRegEx - The route regex pattern
 * @param paramNames - Ordered array of required and optional param names in regex
 * @param routeWithoutExtraParams - route without the extra params
 * @returns If the routeRegEx does not match the route, it returns null.
 * Otherwise returns a map of paramName to paramVal.
 * Optional params that are not present in the route wil have undefined paramVal.
 * All parameter values are url decoded
 */
export function matchRoute(routeRegEx, paramNames, routeWithoutExtraParams) {
  // const {regex, paramNames} = routeTemplateToRegex(routeTemplate);
  if (routeWithoutExtraParams.indexOf("?") >= 0) {
    throw new Error(`Can not pass route with extra params to matchRoute, route: '${routeWithoutExtraParams}'`);
    ;
  }
  const match = routeWithoutExtraParams.match(routeRegEx);
  if (!match) {
    return undefined;
  }
  // Defensive in case routeRegEx and paramNames are out of sync
  if (match.length !== paramNames.length + 1) {
    throw new Error(`Route regex returns ${match.length} matches but expected ${paramNames.length + 1}`);
  }

  // The first element in match array is the entire matched string,
  // subsequent elements are the captured groups
  const params = [];

  // Start from match index 1 to skip the full match in match[0]
  for (let i = 0; i < paramNames.length; i++) {
    // match[i+1] can be undefined for optional parameters
    const matchedParamVal = match[i + 1];
    const paramVal = matchedParamVal ? decodeURIComponent(matchedParamVal) : undefined;
    params.push(paramVal);
  }
  return params;
}
export function buildParamMap(paramNames, paramValues) {
  const params = {};
  paramNames.forEach((paramName, i) => {
    params[paramName] = paramValues[i];
  });
  return params;
}
//# sourceMappingURL=RouteParser.js.map
